import Vue from 'vue'
import Router from 'vue-router'
import { authenticated, user } from '@/services/auth.js'
import { isMobileDevice } from '@/services/utils.js'

Vue.use(Router)

let router = new Router({
  // This is important to set router mode to hash since
  // the application will be deployed to s3 and will
  // be served statically, without server
  base: process.env.BASE_URL,

  // So that anchor links work
  scrollBehavior(to) {
    if (to.hash) {
      return { selector: to.hash }
    } else {
      return { x: 0, y: 0 }
    }
  },

  routes: [
    {
      path: '/',
      name: 'home',
      redirect: () => {
        if (!authenticated()) {
          return { name: 'login' }
        } else {
          if (isMobileDevice()) {
            return { name: 'feed' }
          }

          return { name: 'overview' }
        }
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: "login" */ '@/pages/Login.vue'),
      meta: {
        redirectToHomeIfAuthenticated: true,
        layout: 'auth'
      }
    },
    {
      path: '/overview',
      name: 'overview',
      component: () => import(/* webpackChunkName: "overview" */ '@/pages/Overview.vue'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/feed',
      name: 'feed',
      component: () => import(/* webpackChunkName: "feed" */ '@/pages/Feed.vue'),
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/manage-requests',
      name: 'manageRequests',
      component: () => import(/* webpackChunkName: "manage-requests" */ '@/pages/ManageRequests.vue'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/devices',
      name: 'devices',
      component: () => import(/* webpackChunkName: "devices" */ '@/pages/Mobile/InstalledDevices.vue'),
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/locations',
      name: 'locations',
      component: () => import(/* webpackChunkName: "devices" */ '@/pages/Mobile/GeofenceList.vue'),
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/configure-location',
      name: 'configure-location',
      component: () => import(/* webpackChunkName: "devices" */ '@/pages/Mobile/ConfigureLocation.vue'),
      props: true,
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/password-change',
      name: 'password',
      component: () => import(/* webpackChunkName: "change-password" */ '@/pages/Mobile/ChangePassword.vue'),
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/children/:id',
      name: 'children.single',
      component: {
        // @see https://github.com/vuejs/vue-router/issues/745
        render (c) { return c('router-view') }
      },
      redirect: { name: 'children.single.searches' },
      children: [
        {
          path: 'searches',
          name: 'children.single.searches',
          component: () => import(/* webpackChunkName: "children.single.searches" */ '@/pages/Children/Searches.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresPaidLicense: true,
            layout: 'child'
          }
        },
        {
          path: 'location',
          name: 'children.single.location',
          component: () => import(/* webpackChunkName: "children.single.location" */ '@/pages/Children/Location.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresPaidLicense: true,
            layout: 'child'
          }
        },
        {
          path: 'location-history/:childId',
          name: 'children.single.locationHistory',
          component: () => import(/* webpackChunkName: "location-history" */ '@/pages/Mobile/LocationHistory.vue'),
          props: true,
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'child-profile/:childId',
          name: 'children.single.childProfile',
          component: () => import(/* webpackChunkName: "location-history" */ '@/pages/Mobile/AvatarProfilePicture.vue'),
          props: true,
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'screen-time',
          name: 'children.single.screenTime',
          component: () => import(/* webpackChunkName: "children.single.screenTime" */ '@/pages/Children/ScreenTime.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresPaidLicense: true,
            layout: 'child'
          }
        },
        {
          path: 'youtube',
          name: 'children.single.youtube',
          component: () => import(/* webpackChunkName: "children.single.screenTime" */ '@/pages/Children/YouTube.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresPaidLicense: true,
            layout: 'child'
          }
        },
        {
          path: 'blocks-and-alerts',
          name: 'children.single.blocksAndAlerts',
          component: () => import(/* webpackChunkName: "children.single.blocksAndAlerts" */ '@/pages/Children/BlocksAndAlerts.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresPaidLicense: true,
            layout: 'child'
          }
        },
        {
          path: 'schedule',
          name: 'children.single.schedule',
          component: () => import(/* webpackChunkName: "children.single.schedule" */ '@/pages/Children/Schedule.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresPaidLicense: true,
            layout: 'only-header'
          }
        },
        {
          path: 'app-settings/:childId',
          name: 'children.single.appSettings',
          component: () => import(/* webpackChunkName: "app-settings" */ '@/pages/Mobile/AppSettings.vue'),
          props: true,
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'content-filter/:childId',
          name: 'children.single.contentFilter',
          component: () => import(/* webpackChunkName: "content-filter" */ '@/pages/Mobile/ContentFilter.vue'),
          props: true,
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'web-settings/:childId',
          name: 'children.single.webSettings',
          component: () => import(/* webpackChunkName: "web-settings" */ '@/pages/Mobile/WebSettings.vue'),
          props: true,
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'screen-time-management/:childId',
          name: 'children.single.screenTimeManagement',
          component: () => import(/* webpackChunkName: "screen-time-management" */ '@/pages/Mobile/ScreenTime.vue'),
          props: true,
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'custom-filters-list/:modeId/:childId',
          name: 'children.single.customFilters',
          component: () => import(/* webpackChunkName: "custom-filters" */ '@/pages/Mobile/CustomFiltersList.vue'),
          props: true,
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'custom-filters-editor/:modeId/:childId',
          name: 'children.single.customFiltersEditor',
          component: () => import(/* webpackChunkName: "custom-filters-editor" */ '@/pages/Mobile/CustomFiltersEditor.vue'),
          props: true,
          meta: {
            requiresAuth: true
          }
        }
      ]
    },
    {
      path: '/faq',
      name: 'faq',
      component: () => import(/* webpackChunkName: "login" */ '@/pages/Faq.vue'),
      meta: {
        requiresAuth: true
      }
    }
  ]
})

router.beforeEach((to, from, next) => {
  let requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  let redirectToHomeIfAuthenticated = to.matched.some(record => record.meta.redirectToHomeIfAuthenticated)
  let requiresPaidLicense = to.matched.some(record => record.meta.requiresPaidLicense)

  if (authenticated()) {
    if (redirectToHomeIfAuthenticated) {
      next({
        name: 'home'
      })
    } else if (requiresPaidLicense) {
      if (user().hasPaidLicense) {
        next()
      } else {
        next({
          name: 'home'
        })
      }
    } else {
      next()
    }
  } else {
    if (requiresAuth) {
      next({
        name: 'home'
      })
    } else {
      next()
    }
  }
})

export default router
